/**
 * TrainingPanel — Per-court collapsible "Entrenamiento" panel.
 * Allows Profesor / Directivo / Admin to log a training session for the court
 * with all the parameters the user requested (kind, modality, focus, player,
 * handedness, chronometer warmup, baskets/balls, ball type, temp, humidity).
 *
 * Renders inline inside a court tile in ClubDetail.
 */
import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Barbell, CaretDown, CaretUp, Trash, Robot } from "@phosphor-icons/react";

const KIND = [{ value: "clinica", label: "Clínica" }, { value: "clase", label: "Clase" }];
const MODALITY = [
  { value: "grupal", label: "Grupal" },
  { value: "personal", label: "Personal" },
  { value: "dupla", label: "Dupla" },
];
const FOCUS = [
  { value: "fisico", label: "Físico" },
  { value: "tecnico", label: "Técnico" },
  { value: "mecanico", label: "Mecánico" },
  { value: "jugada", label: "Jugada" },
];
const HAND = [
  { value: "diestro", label: "Diestro" },
  { value: "zurdo", label: "Zurdo" },
  { value: "ambidiestro", label: "Ambidiestro" },
];
const BALL_TYPE = [
  { value: "no_presurizadas", label: "No presurizadas" },
  { value: "presurizadas", label: "Presurizadas" },
  { value: "con_peso", label: "Con peso" },
];
const WARMUP_PRESETS = [5, 15, 30];

const EMPTY = {
  kind: "clase",
  modality: "personal",
  focus: "tecnico",
  player_name: "",
  player_handedness: "diestro",
  chronometer_sec: 0,
  warmup_min: 5,
  baskets_count: 0,
  balls_count: 0,
  ball_type: "presurizadas",
  temperature_c: "",
  humidity_pct: "",
  notes: "",
};

export default function TrainingPanel({ clubId, courtName, canManage = false, testId }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [submitting, setSubmitting] = useState(false);
  const [aiInsights, setAiInsights] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(() => {
    if (!open) return;
    const params = new URLSearchParams({ club_id: clubId, court_name: courtName });
    api.get(`/training?${params.toString()}`)
      .then(({ data }) => setSessions(data || []))
      .catch(() => setSessions([]));
  }, [open, clubId, courtName]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!canManage) {
      toast.error("Solo profesor/directivo");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        club_id: clubId,
        court_name: courtName,
        kind: form.kind,
        modality: form.modality,
        focus: form.focus,
        player_name: form.player_name,
        player_handedness: form.player_handedness,
        chronometer_sec: parseInt(form.chronometer_sec) || 0,
        warmup_min: parseInt(form.warmup_min) || 0,
        baskets_count: parseInt(form.baskets_count) || 0,
        balls_count: parseInt(form.balls_count) || 0,
        ball_type: form.ball_type,
        temperature_c: form.temperature_c === "" ? null : parseFloat(form.temperature_c),
        humidity_pct: form.humidity_pct === "" ? null : parseFloat(form.humidity_pct),
        notes: form.notes,
      };
      if (user?.role === "profesor") {
        payload.professor_id = user.id;
        payload.professor_name = user.name;
      }
      await api.post("/training", payload);
      toast.success("Sesión registrada");
      setForm({ ...EMPTY });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm("¿Eliminar sesión?")) return;
    try { await api.delete(`/training/${s.id}`); toast.success("Eliminada"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const analyzeAI = async () => {
    setAiLoading(true);
    setAiInsights("");
    try {
      const { data } = await api.post("/training-ai/analyze", { club_id: clubId, court_name: courtName, limit: 30 });
      setAiInsights(data.insights || "Sin análisis disponible.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error analizando");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="border border-white/10 bg-[#0A0A0A]/40" data-testid={testId || `training-panel-${courtName}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#CCFF00]/5"
        data-testid={`training-toggle-${courtName}`}
      >
        <Barbell size={14} weight="duotone" className="text-[#CCFF00]" />
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#CCFF00] flex-1 text-left">Entrenamiento</span>
        {sessions.length > 0 && (
          <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[9px]">{sessions.length}</Badge>
        )}
        {open ? <CaretUp size={12} className="text-gray-400" /> : <CaretDown size={12} className="text-gray-400" />}
      </button>

      {open && (
        <div className="p-3 space-y-2 border-t border-white/5">
          {canManage ? (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[9px] uppercase tracking-widest text-gray-500">Tipo</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 h-8 text-xs" data-testid={`training-kind-${courtName}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{KIND.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[9px] uppercase tracking-widest text-gray-500">Modalidad</Label>
                  <Select value={form.modality} onValueChange={(v) => setForm({ ...form, modality: v })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 h-8 text-xs" data-testid={`training-modality-${courtName}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{MODALITY.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[9px] uppercase tracking-widest text-gray-500">Enfoque</Label>
                  <Select value={form.focus} onValueChange={(v) => setForm({ ...form, focus: v })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 h-8 text-xs" data-testid={`training-focus-${courtName}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{FOCUS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Jugador (VAJ, LA…)" value={form.player_name} onChange={(e) => setForm({ ...form, player_name: e.target.value })} className="bg-[#0A0A0A] border-white/10 h-8 text-xs" data-testid={`training-player-${courtName}`} />
                <Select value={form.player_handedness} onValueChange={(v) => setForm({ ...form, player_handedness: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 h-8 text-xs" data-testid={`training-hand-${courtName}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{HAND.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[9px] uppercase tracking-widest text-gray-500">Calentamiento (min)</Label>
                <div className="flex gap-1">
                  {WARMUP_PRESETS.map((min) => (
                    <button
                      key={min}
                      type="button"
                      onClick={() => setForm({ ...form, warmup_min: min })}
                      className={`flex-1 px-2 py-1 text-xs font-semibold border ${form.warmup_min === min ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] border-white/10 text-gray-400 hover:border-[#CCFF00]/30"}`}
                      data-testid={`training-warmup-${min}-${courtName}`}
                    >
                      {min}'
                    </button>
                  ))}
                  <Input type="number" min="0" value={form.warmup_min} onChange={(e) => setForm({ ...form, warmup_min: e.target.value })} className="bg-[#0A0A0A] border-white/10 h-7 text-xs w-16" data-testid={`training-warmup-custom-${courtName}`} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input type="number" min="0" placeholder="C/ Cesto" value={form.baskets_count} onChange={(e) => setForm({ ...form, baskets_count: e.target.value })} className="bg-[#0A0A0A] border-white/10 h-8 text-xs" data-testid={`training-baskets-${courtName}`} />
                <Input type="number" min="0" placeholder="C/ Pelotas" value={form.balls_count} onChange={(e) => setForm({ ...form, balls_count: e.target.value })} className="bg-[#0A0A0A] border-white/10 h-8 text-xs" data-testid={`training-balls-${courtName}`} />
                <Select value={form.ball_type} onValueChange={(v) => setForm({ ...form, ball_type: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 h-8 text-xs" data-testid={`training-ball-type-${courtName}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{BALL_TYPE.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input type="number" placeholder="Temp °C" value={form.temperature_c} onChange={(e) => setForm({ ...form, temperature_c: e.target.value })} className="bg-[#0A0A0A] border-white/10 h-8 text-xs" data-testid={`training-temp-${courtName}`} />
                <Input type="number" placeholder="Humedad %" value={form.humidity_pct} onChange={(e) => setForm({ ...form, humidity_pct: e.target.value })} className="bg-[#0A0A0A] border-white/10 h-8 text-xs" data-testid={`training-humidity-${courtName}`} />
                <Input type="number" min="0" placeholder="Cronómetro (s)" value={form.chronometer_sec} onChange={(e) => setForm({ ...form, chronometer_sec: e.target.value })} className="bg-[#0A0A0A] border-white/10 h-8 text-xs" data-testid={`training-chrono-${courtName}`} />
              </div>
              <Input placeholder="Notas (opcional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0A] border-white/10 h-8 text-xs" data-testid={`training-notes-${courtName}`} />
              <Button size="sm" onClick={save} disabled={submitting} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs w-full h-8" data-testid={`training-save-${courtName}`}>
                {submitting ? "Guardando…" : "Registrar sesión"}
              </Button>
            </div>
          ) : (
            <p className="text-[10px] text-gray-500 italic">Solo profesor / directivo puede registrar sesiones.</p>
          )}

          {/* History list */}
          {sessions.length > 0 && (
            <>
              <div className="space-y-1 mt-2 max-h-48 overflow-y-auto pr-1 border-t border-white/5 pt-2" data-testid={`training-history-${courtName}`}>
                {sessions.slice(0, 20).map((s) => (
                  <div key={s.id} className="flex items-center gap-2 bg-[#0A0A0A] border border-white/5 px-2 py-1 text-[10px]">
                    <span className="text-[#CCFF00] font-bold uppercase">{s.kind}</span>
                    <span className="text-gray-400">{s.modality}/{s.focus}</span>
                    <span className="text-white truncate flex-1">{s.player_name || "—"}</span>
                    <span className="text-gray-500 font-mono">{s.warmup_min}'C · {s.balls_count}p</span>
                    {(user?.role === "admin" || user?.id === s.created_by) && (
                      <button onClick={() => remove(s)} className="text-gray-500 hover:text-red-400" data-testid={`training-delete-${s.id}`}>
                        <Trash size={10} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {/* DYD Coach AI Insights */}
              {canManage && (
                <div className="mt-2 border-t border-white/5 pt-2">
                  <Button size="sm" onClick={analyzeAI} disabled={aiLoading} variant="outline" className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 font-bold uppercase text-[10px] w-full h-7" data-testid={`training-analyze-ai-${courtName}`}>
                    <Robot size={11} className="mr-1" weight="duotone" /> {aiLoading ? "Analizando…" : "DYD: Analizar patrones"}
                  </Button>
                  {aiInsights && (
                    <div className="mt-2 p-2 bg-[#CCFF00]/10 border border-[#CCFF00]/30 text-[10px] text-gray-200 whitespace-pre-wrap leading-relaxed" data-testid={`training-ai-insights-${courtName}`}>
                      {aiInsights}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
